import { useState, useEffect, useContext } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppContext, SttDialog, SttForm } from '../../../sporttia/all';
import { subsetObject } from '../../../lib/utils';
import { PERMISSIONS } from '../../../lib/permissions';

type SCAdmin = {
    id?: string;
    name?: string;
    email?: string;
    login?: string;
    password?: string;
    token?: string;
};

type Props = {
    scAdmin?: SCAdmin | null;
    onClose: (admin: SCAdmin) => void;
};

export default function SCAdminDialog({
    scAdmin = null,
    onClose,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [internalAdmin, setInternalAdmin] = useState<SCAdmin>();

    /**
     * Handle form
     */
    const handleForm = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) => {
        if (!name) return;
        setInternalAdmin({ ...internalAdmin, [name]: value });
    };

    /**
     * Input admin changes
     */
    useEffect(() => {
        setInternalAdmin(scAdmin ?? undefined);
    }, [scAdmin]);

    /**
     * Save admin
     */
    const saveSCAdmin = () => {
        if (!internalAdmin) return;
        const params = subsetObject(internalAdmin, 'name,email,login,password');

        // The API stores an empty password verbatim, locking the account out.
        if (!params.password) {
            delete params.password;
        }

        // Updating
        if (internalAdmin.id) {
            cxt?.api('PUT', `/scs/admins/${internalAdmin.id}`, {
                params,
                success: (r: { scAdmin: SCAdmin }) => {
                    cxt?.showMessage('S', t('Saved'));
                    onClose(r.scAdmin);
                },
            });

            // Creating
        } else {
            cxt?.api('POST', `/scs/${cxt?.sc?.id}/admins`, {
                params,
                success: (r: { scAdmin: SCAdmin }) => {
                    cxt?.showMessage('S', t('Saved'));
                    onClose(r.scAdmin);
                },
            });
        }
    };

    // -----| Render |-----
    return (
        <>
            {internalAdmin && (
                <SttDialog
                    title={t('Administrator')}
                    open={scAdmin !== null}
                    onClose={() => onClose(internalAdmin)}
                    content={
                        <>
                            <SttForm
                                onChangeForm={handleForm}
                                elements={[
                                    {
                                        md: 12,
                                        sm: 12,
                                        xs: 12,
                                        name: 'name',
                                        caption: t('Name'),
                                        value: internalAdmin.name,
                                    },
                                    {
                                        md: 12,
                                        sm: 12,
                                        xs: 12,
                                        name: 'email',
                                        caption: t('Email'),
                                        value: internalAdmin.email,
                                    },
                                    {
                                        md: 6,
                                        sm: 6,
                                        xs: 12,
                                        name: 'login',
                                        caption: t('Login'),
                                        value: internalAdmin.login,
                                    },
                                    {
                                        md: 6,
                                        sm: 6,
                                        xs: 12,
                                        name: 'password',
                                        caption: t('account.newPassword'),
                                        password: true,
                                        value: internalAdmin.password,
                                    },
                                ]}
                            />

                            {/* --- token --- */}
                            {scAdmin && (
                                <Box mt={3}>
                                    <Typography variant="caption">
                                        Token: {scAdmin.token}
                                    </Typography>
                                </Box>
                            )}
                        </>
                    }
                    buttons={[
                        {
                            type: 'save',
                            onClick: () => saveSCAdmin(),
                            permission: internalAdmin?.id
                                ? PERMISSIONS.ADMINISTRATION.MODIFY
                                : PERMISSIONS.ADMINISTRATION.CREATE,
                        },
                    ]}
                />
            )}
        </>
    );
}
